import cx from 'classnames';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import Transition from 'react-transition-group/Transition';

import BackButton from '../../../ui/back-button';
import Button from '../../../ui/button';
import Filter2Icon from '../../../ui/icons/filter2';
import BPModal from '../../../ui/modal';

import classes from './mobile-filters.module.css';
import BPImage from '../../../ui/v2/image';
import Filter3Icon from '../../../ui/icons/filter3';
import { genericSideBarIsOpenAtom } from '../../../../hooks/use-generic-sidebar';
import {
  actualStepAtom,
  catalogueGroupsAtom,
  currentSelectionAtom,
  isCatalogueMenuOpenAtom,
  selectedCatalogueSelectionAtom,
} from '../../../../state/selection';
import Sort from '../sort';
import useLinkProps from '../../../../hooks/use-link-props';
import CatalogueWithtSelectedPart from '../../../ui/icons/catalogue-with-selected-part';
import CatalogueWithoutSelectedPart from '../../../ui/icons/catalogue-without-selected-part';
import CatalogueWithSelectedPart from '../../../ui/icons/catalogue-with-selected-part';

const VehicleSelectionMobile = dynamic(
  () => import('../../../ui/legacy/inputs/filters/vehicle/modal'),
  {
    ssr: false,
  }
);

type TViews = null | 'parts' | 'vehicle';

export const mobileFiltersIsOpenAtom = atom(false);
export const mobileFiltersSetOpenAtom = atom(null, (_, set, isOpen) => {
  set(mobileFiltersIsOpenAtom, isOpen);
  if (!isOpen) set(mobileFiltersViewAtom, null);
});
export const mobileFiltersViewAtom = atom<TViews>(null);

const MobileFilters = ({
  onClose,
  filters,
  selectedPart,
  groups,
  isReferencePage = false,
}: {
  onClose: () => void;
  filters: {
    part: ReactNode;
  };
  selectedPart: any;
  groups: TCategories;
  isReferencePage: boolean;
}) => {
  const { t } = useTranslation();
  const { query, push } = useRouter();
  const getLink = useLinkProps();
  const isOpen = useAtomValue(mobileFiltersIsOpenAtom);
  const setIsOpen = useSetAtom(mobileFiltersSetOpenAtom);
  const [isFirstOpen, setIsFirstOpen] = useState(false);

  const [, setOpen] = useAtom(isCatalogueMenuOpenAtom);
  const [, setSelectedCatalogueSelection] = useAtom(
    selectedCatalogueSelectionAtom
  );
  const [, setActualStep] = useAtom(actualStepAtom);
  const [, setFilteredGroups] = useAtom(catalogueGroupsAtom);

  useEffect(() => {
    if (isOpen && !isFirstOpen) {
      setIsFirstOpen(true);
    }
  }, [isOpen, isFirstOpen]);

  const openCatalogueSidebar = () => {
    setFilteredGroups(groups);
    setOpen(true);
    if (selectedPart) {
      setSelectedCatalogueSelection(selectedPart);
      setActualStep(1);
    } else {
      setSelectedCatalogueSelection(null);
      setActualStep(0);
    }
  };

  const openCatalogueAvailable = groups.filter(
    (group) => group.total_products > 0
  );

  return (
    <>
      <div className="flex w-full justify-between shadow px-4">
        <Button
          onClick={openCatalogueSidebar}
          isFullWidth
          className={cx('bg-white', 'text-left', 'items-center')}
          data-cy="btn-mobile-filters"
          type="button"
          disabled={openCatalogueAvailable.length === 0}
        >
          <div className="w-full flex flex-row gap-2 items-center justify-start">
            {selectedPart ? (
              <>
                <CatalogueWithSelectedPart
                  className={cx('h-4', 'w-4', {
                    'text-neutral-50': openCatalogueAvailable.length === 0,
                  })}
                />
                <span className="text-sm">{t('common:change-part')}</span>
              </>
            ) : (
              <>
                <CatalogueWithoutSelectedPart
                  className={cx('h-4', 'w-4', {
                    'text-neutral-50': openCatalogueAvailable.length === 0,
                  })}
                />
                <span className="text-sm">{t('common:select-part')}</span>
              </>
            )}
          </div>
        </Button>

        <div className="relative">
          <Sort
            hideMobile={false}
            hideDesktop={false}
            onChange={(e) => {
              const { sort: currentSort, page, ...rest } = query;
              const value = e.currentTarget.value;

              const link = getLink(isReferencePage ? 'reference' : 'products', {
                ...rest,
                ...(value === 'price-asc' ? {} : { sort: value }),
              });
              push(link.href, link.as, {
                shallow: true,
              });
            }}
          />
        </div>
      </div>
    </>
  );
};

export default MobileFilters;
